import { lt, showSuccessAlert } from '@mews/framework';
import { AsyncAction, CommanderThunkAction, WithOwnerId } from '@mews-commander/types';
import { IdentityDocumentType } from '@mews-commander/data-access';
import { Identifiable, Optional } from '@mews/std';
import { getWallClockDateUtcString } from '@mews-commander/utils';
import { getIdentityDocumentTitle } from '@mews-commander/legacy-entities/src/ducks/identityDocuments';
import {
    addDocument,
    updateDocument,
    deleteDocument,
} from '@mews-commander/legacy-entities/src/actions/identityDocuments';
import { createAddAction, createEditAction, createDeleteAction } from '../entityManagement';
import { changePaginationIds } from '../pagination/actions';
import { getOwnerSortedIdentityDocumentsIds } from './selectors';

export interface IdentityDocumentFormValues {
    issuingCountryCode: string | null;
    issuingCity: string | null;
    number: string | null;
    type: IdentityDocumentType;
    issueDateString: Optional<string>;
    expiryDateString: Optional<string>;
    documentSupportNumber: string | null;
}

const changeDocumentList =
    (ownerId: string, scope: string): CommanderThunkAction =>
    (dispatch, getState) => {
        const ids = getOwnerSortedIdentityDocumentsIds(getState(), { ownerId });
        dispatch(changePaginationIds(ids, scope));
    };

type MapDocumentProps = IdentityDocumentFormValues & Identifiable & WithOwnerId;

const mapDocument =
    (action: typeof addDocument | typeof updateDocument) =>
    ({ issueDateString, expiryDateString, ...other }: MapDocumentProps): CommanderThunkAction =>
    async (dispatch) => {
        const issueDateUtc = getWallClockDateUtcString(issueDateString);
        const expiryDateUtc = getWallClockDateUtcString(expiryDateString);

        return await dispatch(action({ issueDateUtc, expiryDateUtc, ...other }));
    };

export const addIdentityDocument = mapDocument(addDocument);
export const editIdentityDocument = mapDocument(updateDocument);

const customerAdder = createAddAction(addIdentityDocument);
export const addDocumentAction =
    (values: IdentityDocumentFormValues, ownerId: string, scope: string): CommanderThunkAction<AsyncAction> =>
    (dispatch) =>
        dispatch(customerAdder({ ...values, ownerId }, scope)).then(() => {
            dispatch(changeDocumentList(ownerId, scope));
            showSuccessAlert({ description: lt('SaveSuccessMessage') });
        });

const customerEditor = createEditAction(editIdentityDocument);
export const editDocumentAction =
    (values: IdentityDocumentFormValues, ownerId: string, scope: string): CommanderThunkAction<AsyncAction> =>
    (dispatch) =>
        dispatch(customerEditor({ ...values, ownerId }, scope)).then(() => {
            showSuccessAlert({ description: lt('SaveSuccessMessage') });
        });

const customerDeletor = createDeleteAction(deleteDocument);
export const deleteDocumentAction =
    (ownerId: string, scope: string): CommanderThunkAction =>
    (dispatch) =>
        dispatch(customerDeletor({ ownerId }, scope)).then((deletedIdentityDocument: $TSFixMe) => {
            const identityDocumentTitle = getIdentityDocumentTitle(deletedIdentityDocument);
            dispatch(changeDocumentList(ownerId, scope));
            showSuccessAlert({
                description: lt('DeleteSuccessMessage', { entity: identityDocumentTitle }),
            });
        });
